import { PageEyebrow } from '../components/PageContextLabel';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaApple } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import {
  loginWithGoogle,
  loginWithApple,
  isAuthenticated,
  hasAdminRole,
  startSessionRenewal,
  logout,
} from '../utils/auth';
import { loadScript } from '../utils/loadScript';
import AnimatedLogo from '../components/AnimatedLogo';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const APPLE_SERVICE_ID = process.env.REACT_APP_APPLE_SERVICE_ID || '';
const APPLE_REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI || '';

const GOOGLE_GSI_SRC = 'https://accounts.google.com/gsi/client';
const APPLE_JS_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [appleReady, setAppleReady] = useState(false);
  // Hover feedback for the provider buttons. They are inline-styled, so the
  // hover state lives in React rather than in a :hover rule.
  const [hovered, setHovered] = useState<'apple' | 'google' | null>(null);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  // Surface OAuth errors passed back via query params (e.g. access_denied).
  useEffect(() => {
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    if (!errorParam) return;

    if (errorParam === 'access_denied') {
      setError('Access denied. Only admins can access the Admin Portal.');
    } else if (errorDescription) {
      setError(decodeURIComponent(errorDescription));
    } else {
      setError(`Authentication error: ${errorParam}`);
    }
  }, [searchParams]);

  // Finish login: verify admin access, then enter the dashboard.
  const finishLogin = useCallback(async () => {
    if (!isAuthenticated() || !hasAdminRole()) {
      await logout();
      setError('You are not authorized to access the Admin Portal.');
      setBusy(false);
      return;
    }
    startSessionRenewal();
    navigate('/dashboard', { replace: true });
  }, [navigate]);

  // Google Identity Services — initialise and render the official button.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;

    loadScript(GOOGLE_GSI_SRC)
      .then(() => {
        if (cancelled) return;
        const google = (window as any).google;
        if (!google?.accounts?.id || !googleButtonRef.current) return;

        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: async (response: { credential?: string }) => {
            if (!response.credential) return;
            setBusy(true);
            setError(null);
            try {
              await loginWithGoogle(response.credential);
              await finishLogin();
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Google login failed.');
              setBusy(false);
            }
          },
        });

        google.accounts.id.renderButton(googleButtonRef.current, {
          theme: 'outline',
          size: 'large',
          shape: 'pill',
          width: 320,
          text: 'continue_with',
          logo_alignment: 'center',
        });
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load Google sign-in.');
      });

    return () => {
      cancelled = true;
    };
  }, [finishLogin]);

  // Apple JS — initialise so the button can open the sign-in popup.
  useEffect(() => {
    if (!APPLE_SERVICE_ID || !APPLE_REDIRECT_URI) return;
    let cancelled = false;

    loadScript(APPLE_JS_SRC)
      .then(() => {
        if (cancelled) return;
        const AppleID = (window as any).AppleID;
        if (!AppleID?.auth) return;
        AppleID.auth.init({
          clientId: APPLE_SERVICE_ID,
          scope: 'name email',
          redirectURI: APPLE_REDIRECT_URI,
          usePopup: true,
        });
        setAppleReady(true);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load Apple sign-in.');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleApple = async () => {
    const AppleID = (window as any).AppleID;
    if (!AppleID?.auth) return;
    setBusy(true);
    setError(null);
    try {
      const data = await AppleID.auth.signIn();
      const idToken = data?.authorization?.id_token;
      if (!idToken) {
        throw new Error('Apple did not return a valid token.');
      }
      await loginWithApple(idToken, data?.user);
      await finishLogin();
    } catch (err: any) {
      if (err?.error === 'popup_closed_by_user' || err?.error === 'user_cancelled_authorize') {
        setBusy(false);
        return;
      }
      setError(err instanceof Error ? err.message : 'Apple login failed.');
      setBusy(false);
    }
  };

  const showGoogle = Boolean(GOOGLE_CLIENT_ID);
  const showApple = Boolean(APPLE_SERVICE_ID && APPLE_REDIRECT_URI);

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        backgroundColor: 'var(--color-bg)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 448,
          padding: 32,
          textAlign: 'center',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <AnimatedLogo size={120} />
        </div>

        <PageEyebrow section="Account" label="Sign in" />
        <h1 style={{ margin: '0 0 8px', fontSize: '1.75rem', fontWeight: 600, color: 'var(--color-text)' }}>
          Admin Portal
        </h1>
        <p style={{ margin: '0 0 24px', fontSize: '1rem', color: 'var(--color-muted)' }}>
          Sign in to manage FRAW — admins only
        </p>

        {error && (
          <div
            role="alert"
            style={{
              display: 'flex',
              gap: 8,
              marginBottom: 24,
              padding: 16,
              borderRadius: 8,
              border: '1px solid var(--color-danger)',
              backgroundColor: 'rgba(248, 81, 73, 0.1)',
              color: 'var(--color-danger)',
              fontSize: '0.875rem',
              lineHeight: 1.375,
              textAlign: 'left',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }}>
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            opacity: busy ? 0.6 : 1,
            pointerEvents: busy ? 'none' : 'auto',
          }}
        >
          {/* Apple above Google, matching the mobile login sheet. */}
          {showApple && (
            <button
              type="button"
              onClick={handleApple}
              onMouseEnter={() => setHovered('apple')}
              onMouseLeave={() => setHovered(null)}
              disabled={!appleReady || busy}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                minHeight: 44,
                width: '100%',
                maxWidth: 320,
                padding: '8px 20px',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'black',
                backgroundColor: 'white',
                border: 'none',
                borderRadius: 9999,
                cursor: appleReady && !busy ? 'pointer' : 'not-allowed',
                opacity: !appleReady || busy ? 0.5 : hovered === 'apple' ? 0.9 : 1,
                transition: 'opacity 150ms',
              }}
            >
              <FaApple size={20} aria-hidden="true" />
              Sign in with Apple
            </button>
          )}

          {/* Custom-styled Google button with the real GSI button overlaid invisibly on top,
              so it captures the click (and the ID-token flow) while we control the label/style. */}
          {showGoogle && (
            <div
              onMouseEnter={() => setHovered('google')}
              onMouseLeave={() => setHovered(null)}
              style={{
                position: 'relative',
                width: '100%',
                maxWidth: 320,
                height: 44,
                opacity: hovered === 'google' ? 0.9 : 1,
                transition: 'opacity 150ms',
              }}
            >
              <div
                aria-hidden
                style={{
                  display: 'inline-flex',
                  boxSizing: 'border-box',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  width: '100%',
                  height: '100%',
                  padding: '8px 20px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'black',
                  backgroundColor: 'white',
                  borderRadius: 9999,
                }}
              >
                <FcGoogle size={20} aria-hidden="true" />
                Continue with Google
              </div>
              <div
                ref={googleButtonRef}
                style={{ position: 'absolute', inset: 0, opacity: 0, overflow: 'hidden' }}
              />
            </div>
          )}

          {!showGoogle && !showApple && (
            <p style={{ fontSize: '0.875rem', color: 'var(--color-muted)' }}>Social sign-in is not configured.</p>
          )}
        </div>
      </div>
    </div>
  );
}
